import type { ReactNode } from 'react';
import { Icon } from '../common/Icon';

// ─── List container ────────────────────────────────────────────────────────

export interface ListProps {
  children: ReactNode;
  /** Visual variant. Default `plain` is a continuous list with hairline
   * dividers between rows (the WeChat conversation list pattern).
   * `inset` adds inner left padding so dividers appear to start after
   * the avatar column — the iOS Settings indented-divider look. */
  variant?: 'plain' | 'inset';
}

/**
 * Container for a list of `<ListRow>`s. Owns the divider strategy so
 * pages don't have to remember `divide-y divide-gray-100 dark:divide-white/[0.06]`
 * every time.
 *
 * @example
 * <List>
 *   <ListRow ... />
 *   <ListRow ... />
 * </List>
 */
export function List({ children, variant = 'plain' }: ListProps) {
  const dividerClass =
    variant === 'inset'
      ? '' // Inset variant relies on per-row inner border (see ListRow's `inset` mode)
      : 'divide-y divide-gray-100 dark:divide-white/[0.06]';

  return <div className={dividerClass}>{children}</div>;
}

// ─── List row ──────────────────────────────────────────────────────────────

export interface ListRowProps {
  /** Leading slot — 40×40. Avatar, icon tile, or thumbnail. Pass
   * `null` for a dense settings-style row with no leading element. */
  avatar?: ReactNode;
  /** Title row. String for the simple case; ReactNode when the title
   * mixes inline elements (badges, mono code, copy button). */
  title: ReactNode;
  /** One-line subtitle in the secondary text color. Truncates. */
  subtitle?: ReactNode;
  /** Right-aligned metadata column. Stacks vertically — typically a
   * timestamp on top and a small badge/count below, mirroring WeChat
   * conversation rows. */
  meta?: ReactNode;
  /** Drill-in chevron on the far right. Use for rows that navigate
   * deeper. Don't use on chat-style rows. */
  chevron?: boolean;
  /** Tap handler. Without this the row is non-interactive. */
  onClick?: () => void;
  /** Extra Tailwind classes applied to the row root. Use sparingly —
   * mainly for emphasis treatments like a tinted background on a
   * pinned/special row. */
  className?: string;
}

/**
 * The canonical list row used everywhere — chat lists, settings,
 * leaderboards, drill-down navigation, member rosters.
 *
 * Layout:
 *
 *     ┌─────┬─────────────────────────┬──────────┬───┐
 *     │ av  │ title (truncate)        │ meta-top │ › │
 *     │ 40  │ subtitle                │ meta-bot │   │
 *     └─────┴─────────────────────────┴──────────┴───┘
 *      ↑          ↑                       ↑      ↑
 *      40px       gap-3                  meta   chevron
 *
 * Hover and active fills cover the full row including the avatar
 * column. Hairline divider lives on the parent `<List>`.
 *
 * @example
 * <ListRow
 *   avatar={<AgentAvatar avatar={a.avatar} status={a.status} size="md" />}
 *   title={a.name}
 *   subtitle={a.lastMessage}
 *   meta={<Time>{a.timeAgo}</Time>}
 *   chevron
 *   onClick={() => navigate(`/agent/${a.id}`)}
 * />
 */
export function ListRow({
  avatar,
  title,
  subtitle,
  meta,
  chevron,
  onClick,
  className,
}: ListRowProps) {
  return (
    <div
      onClick={onClick}
      className={`
        group flex items-center gap-3 px-4 py-3
        ${onClick
          ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] active:bg-gray-100 dark:active:bg-white/[0.06] transition-colors'
          : ''}
        ${className ?? ''}
      `}
    >
      {avatar && <div className="flex-shrink-0">{avatar}</div>}
      <div className="flex-1 min-w-0">
        {typeof title === 'string' ? (
          <div className="text-base font-medium text-gray-900 dark:text-gray-100 truncate">
            {title}
          </div>
        ) : (
          title
        )}
        {subtitle != null && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
      {meta != null && (
        <div className="flex-shrink-0 flex flex-col items-end gap-0.5 text-[11px] text-gray-400 dark:text-gray-500">
          {meta}
        </div>
      )}
      {chevron && (
        <Icon
          name="chevron_right"
          size={18}
          className="flex-shrink-0 text-gray-300 dark:text-gray-600 -mr-1"
        />
      )}
    </div>
  );
}
